import { Component, effect, Inject, Signal, OnDestroy, computed, signal, WritableSignal } from '@angular/core';
import { MaterialModule } from '@app/material.module';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { InvoiceDataService } from '@app/services/entities/invoice.service';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SeeInfoDialogComponent } from './see-info-dialog/see-info-dialog.component';
import { FormsModule } from '@angular/forms';
import { Factura, TableStructureModel, ColumnsModel, SheetsModel } from '@app/types';
import { Router } from '@angular/router';
import { SimpleExcelService } from '@app/services/utils/simple-excel.service';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';
import { CommonModule, DatePipe } from '@angular/common';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
    selector: 'app-invoice-dashboard',
    imports: [MaterialModule, FormsModule, CommonModule],
    providers: [DatePipe, StatusTranslatorService],
    templateUrl: './invoice-dashboard.component.html',
    styleUrl: './invoice-dashboard.component.scss',
})
export class InvoiceDashboardComponent implements OnDestroy {
    isLoadingSignal: Signal<boolean>;
    isComputingSignal: WritableSignal<boolean> = signal(false); // Señal local para procesamiento de filtros
    filterFacturas: any[] = [];
    facturasStatus: any[] = [];

    clients: any[] = [];
    emisores: any[] = [];

    cleanFilter: boolean = true;

    // Grupos base (fallback si el catálogo no ha cargado o no tiene dashboardGroup)
    ogStatus = [
        { status: 'Validación de Remisión', count: 0, color: 'primary', icon: 'check_circle', percent: 0, stage: '#1976d2' },
        { status: 'Procesada', statuses: ['Facturada', 'Cancelada', 'Rechazada'], count: 0, color: 'info', icon: 'sync_alt', percent: 0, stage: '#6f42c1' },
        { status: 'Tesorería', count: 0, color: 'neutral', icon: 'gavel', percent: 0, stage: '#fd7e14' },
        { status: 'Contabilidad', count: 0, color: 'error', icon: 'highlight_off', percent: 0, stage: '#ffc107' },
        { status: 'Lista para Pago', count: 0, color: 'success', icon: 'attach_money', percent: 0, stage: '#4caf50' },
        { status: 'Pagada', count: 0, color: 'success', icon: 'price_check', percent: 0, stage: '#198754' },
    ];

    // Iconos sugeridos por grupo del dashboard
    private readonly groupIcons: Record<string, string> = {
        'Validación de Remisión': 'check_circle',
        'Procesada': 'sync_alt',
        'Tesorería': 'gavel',
        'Contabilidad': 'highlight_off',
        'Lista para Pago': 'attach_money',
        'Pagada': 'price_check',
        'Otros': 'category',
    };

    /**
     * Grupos del dashboard derivados del catálogo (dashboardGroup + color + order).
     * Fallback a ogStatus si el catálogo no tiene grupos definidos.
     */
    dashboardGroups = computed<any[]>(() => {
        const statuses = this.statusTranslator.getStatuses()().filter(s => !s.deleted);

        const groups = new Map<string, { status: string; statuses: string[]; count: number; color: string; icon: string; percent: number; stage: string; order: number }>();
        for (const s of statuses.sort((a, b) => a.order - b.order)) {
            const group = s.dashboardGroup;
            if (!group) continue; // sin grupo => caerá en "Otros"
            if (!groups.has(group)) {
                groups.set(group, {
                    status: group,
                    statuses: [],
                    count: 0,
                    color: 'primary',
                    icon: this.groupIcons[group] || 'label',
                    percent: 0,
                    stage: s.color || '#1976d2',
                    order: s.order,
                });
            }
            groups.get(group)!.statuses.push(s.status);
        }

        const derived = Array.from(groups.values());
        return derived.length > 0 ? derived : this.ogStatus;
    });

    // Estatus con grupo conocido (para saber qué va a "Otros")
    private groupedStatuses = computed<Set<string>>(() => {
        const set = new Set<string>();
        for (const g of this.dashboardGroups()) {
            if (g.statuses) {
                for (const s of g.statuses) set.add(s);
            } else {
                set.add(g.status);
            }
        }
        return set;
    });

    fullData: any[] = [];
    uiData: any[] = [];

    filteredClients: any;
    clientFilter: any = '';
    filteredEmisores: any;
    emisorFilter: any = '';
    emisionFilter: { begin: Date | null; end: Date | null } = { begin: null, end: null };
    dueFilter: { begin: Date | null; end: Date | null } = { begin: null, end: null };
    
    // Para detectar cambios individuales en las fechas
    private emisionStartDateChange$ = new Subject<void>();
    private emisionEndDateChange$ = new Subject<void>();
    private dueStartDateChange$ = new Subject<void>();
    private dueEndDateChange$ = new Subject<void>();

    private searchSubject = new Subject<void>();
    private destroy$ = new Subject<void>();

    get total(): number {
        return this.uiData.reduce((acc, r) => acc + r.count, 0);
    }

    // [MODIFICACIÓN] Exponemos la señal pura del servicio para el spinner de carga inicial
    public isDataLoadingSignal: Signal<boolean>;

    constructor(
        private invoiceDataService: InvoiceDataService,
        private customMessage: CustomMessageService,
        private customSnackBar: CustomSnackBarService,
        private dialog: MatDialog,
        private simpleExcel: SimpleExcelService,
        private router: Router,
        private datePipe: DatePipe,
        private statusTranslator: StatusTranslatorService
    ) {
        // [MODIFICACIÓN] Asignamos la señal del servicio directamente
        this.isDataLoadingSignal = this.invoiceDataService.isLoadingSignal;
        
        // Combinamos la carga de datos (API) con el procesamiento local (Cálculos) para la lógica interna si es necesario
        this.isLoadingSignal = computed(() => this.invoiceDataService.isLoadingSignal() || this.isComputingSignal());

        // Configurar debounce para búsquedas
        this.searchSubject.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.performSearch();
        });

        // Configurar debounce para cambios individuales en fechas
        this.emisionStartDateChange$.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => this.performSearch());

        this.emisionEndDateChange$.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => this.performSearch());

        this.dueStartDateChange$.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => this.performSearch());

        this.dueEndDateChange$.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => this.performSearch());

        effect(() => {
            const facturas = this.invoiceDataService.facturasSignal();
            if (facturas.length > 0) {
                this.filterFacturas = [...facturas];
                this.getClients();
                this.getEmisores();
                this.generateUiData();
            }
        });
    }

    ngOnInit() {
        this.invoiceDataService.loadInvoices(true).subscribe();
        this.customMessage.open({ title: 'Espere', type: 'info', message: 'Cargando', duration: 500 });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }



    getClients() {
        const facturasParaClientes = this.filterFacturas.length > 0 ? this.filterFacturas : this.invoiceDataService.facturasSignal();
        this.clients = Array.from(new Map(facturasParaClientes.map((o: Factura) => [o.receptorRFC, { value: o.receptorRFC, viewValue: o.receptorNombre }])).values());
        this.filteredClients = this.clients;
    }

    getEmisores() {
        const facturasParaEmisores = this.filterFacturas.length > 0 ? this.filterFacturas : this.invoiceDataService.facturasSignal();
        this.emisores = Array.from(new Map(facturasParaEmisores.map((o: Factura) => [o.emisorRFC, { value: o.emisorRFC, viewValue: o.emisorNombre }])).values());
        this.filteredEmisores = this.emisores;
    }

    generateUiData() {
        this.uiData = [];
        if (this.filterFacturas.length > 0) {
            this.uiData = structuredClone(this.dashboardGroups());
            // Grupo "Otros": captura estatus fuera de los grupos del catálogo
            this.uiData.push({ status: 'Otros', count: 0, color: 'neutral', icon: 'category', percent: 0, stage: '#78909c' });
            this.uiData.forEach(st => {
                st.totalSubtotal = 0;
                st.totalIva = 0;
                st.totalGeneral = 0;
            });

            const groupedSet = this.groupedStatuses();
            this.filterFacturas.forEach((factura) => {
                const match = this.uiData.find((st) => {
                    if (st.statuses) {
                        return st.statuses.includes(factura.estatus);
                    }
                    return st.status === factura.estatus;
                });
                const target = match || (groupedSet.has(factura.estatus) ? null : this.uiData.find(st => st.status === 'Otros'));
                if (target) {
                    target.count++;
                    target.totalSubtotal += factura.subtotal || 0;
                    target.totalIva += factura.iva || 0;
                    target.totalGeneral += factura.total || 0;
                }
            });

            this.uiData.forEach((data) => {
                data.percent = this.filterFacturas.length > 0 ? ((data.count / this.filterFacturas.length) * 100).toFixed(1) : 0;
            });

            // Ocultar el grupo "Otros" si no hay facturas sin clasificar
            this.uiData = this.uiData.filter(st => st.status !== 'Otros' || st.count > 0);

            this.fullData = structuredClone(this.uiData);
            this.agruparFacturas();
        }
    }

    agruparFacturas() {
        this.facturasStatus = [];
        const groupedSet = this.groupedStatuses();
        this.uiData.forEach((data) => {
            let facturasDelGrupo = [];
            if (data.statuses) {
                facturasDelGrupo = this.filterFacturas.filter((f) => data.statuses.includes(f.estatus));
            } else if (data.status === 'Otros') {
                facturasDelGrupo = this.filterFacturas.filter((f) => !groupedSet.has(f.estatus));
            } else {
                facturasDelGrupo = this.filterFacturas.filter((f) => f.estatus === data.status);
            }
            this.facturasStatus.push({ status: data.status, facturas: facturasDelGrupo });
        });
    }

    onStatusClick(item: any) {
        const facturasData = this.facturasStatus.find((fc) => fc.status === item.status);
        if (facturasData) {
            let dialogRef = this.dialog.open(SeeInfoDialogComponent, {
                data: {
                    facturas: facturasData,
                },
            });
            dialogRef.afterClosed().subscribe((result) => { });
        }
    }

    getSubStatusCounts(statuses: string[]): { name: string; count: number }[] {
        const counts: { name: string; count: number }[] = [];
        for (const status of statuses) {
            counts.push({
                name: status,
                count: this.filterFacturas.filter((f) => f.estatus === status).length,
            });
        }
        return counts;
    }

    // Actualizar las listas de autocompletado al escribir
    onFilterChange() {
        // Filtrar opciones de clientes
        const clientInput = this.clientFilter.toLowerCase();
        this.filteredClients = this.clients.filter((f) => f.viewValue.toLowerCase().includes(clientInput));

        // Filtrar opciones de emisores
        const emisorInput = this.emisorFilter.toLowerCase();
        this.filteredEmisores = this.emisores.filter((f) => f.viewValue.toLowerCase().includes(emisorInput));

        // Disparar búsqueda principal con debounce
        this.searchSubject.next();
    }

    // Métodos específicos para cambios en fechas
    onEmisionStartDateChange() {
        this.emisionStartDateChange$.next();
    }

    onEmisionEndDateChange() {
        this.emisionEndDateChange$.next();
    }

    onDueStartDateChange() {
        this.dueStartDateChange$.next();
    }

    onDueEndDateChange() {
        this.dueEndDateChange$.next();
    }

    reloadData() {
        this.cleanFilter = true;
        this.clientFilter = '';
        this.filteredClients = this.clients;
        this.emisorFilter = '';
        this.filteredEmisores = this.emisores;
        this.emisionFilter = { begin: null, end: null };
        this.dueFilter = { begin: null, end: null };
        this.filterFacturas = structuredClone(this.invoiceDataService.facturasSignal());
        this.generateUiData();
    }

    async download() {
        // ... (Lógica de descarga sin cambios)
        let mainStruct: TableStructureModel[] = [
            { columnKey: 'status', columnName: 'Estatus', type: 'string' },
            { columnKey: 'count', columnName: '# de Facturas', type: 'number' },
            { columnKey: 'percent', columnName: 'Porcentaje', type: 'string' },
            { columnKey: 'totalSubtotal', columnName: 'Subtotal', type: 'currency' },
            { columnKey: 'totalIva', columnName: 'IVA', type: 'currency' },
            { columnKey: 'totalGeneral', columnName: 'Total', type: 'currency' },
        ];
        let facturaStruct: TableStructureModel[] = [
            { columnKey: 'uuid', columnName: 'Folio Fiscal (UUID)', type: 'string' },
            { columnKey: 'folioOrden', columnName: 'Orden de Surtimiento', type: 'string' },
            { columnKey: 'fechaEmision', columnName: 'Fecha de Emisión', type: 'date' },
            { columnKey: 'fechaVencimiento', columnName: 'Fecha de Vencimiento', type: 'date' },
            { columnKey: 'receptorNombre', columnName: 'Nombre Receptor', type: 'string' },
            { columnKey: 'receptorRFC', columnName: 'RFC Receptor', type: 'string' },
            { columnKey: 'estatus', columnName: 'Estatus', type: 'string' },
            { columnKey: 'total', columnName: 'Total', type: 'currency' },
        ];

        // Hojas por grupo usando la misma agrupación dinámica que la UI
        const groupedSet = this.groupedStatuses();
        const sheets = this.uiData.map((group) => {
            let data;
            if (group.statuses) {
                data = this.filterFacturas.filter((f) => group.statuses.includes(f.estatus));
            } else if (group.status === 'Otros') {
                data = this.filterFacturas.filter((f) => !groupedSet.has(f.estatus));
            } else {
                data = this.filterFacturas.filter((f) => f.estatus === group.status);
            }
            return {
                sheetName: group.status.replace(/ /g, '_').slice(0, 31),
                columns: facturaStruct,
                data: data,
                header: [group.status, 'Epione'],
                lineBreak: 1,
            };
        });

        const today = new Date().toISOString().slice(0, 10);
        await this.simpleExcel.simpleXLSX({
            fileName: `Cobranza-Resumen-${today}`,
            sheets: [
                {
                    sheetName: 'Resumen',
                    columns: mainStruct,
                    data: this.uiData,
                    header: ['Resumen - invoice-dashboard', `Total de Facturas: ${this.total}`, 'Epione'],
                    lineBreak: 1,
                },
                ...sheets,
            ],
        });
    }

    async downloadEmisorReport(inicio: Date, fin: Date) {
        // ... (Lógica de descarga reporte emisor sin cambios)
        const periodoStr = `${this.datePipe.transform(inicio, 'dd/MM/yyyy')} - ${this.datePipe.transform(fin, 'dd/MM/yyyy')}`;

        const facturasParaReporte = this.filterFacturas.sort((a, b) => {
            const labCompare = a.emisorNombre.localeCompare(b.emisorNombre);
            if (labCompare !== 0) return labCompare;
            return new Date(b.fechaEmision).getTime() - new Date(a.fechaEmision).getTime();
        });

        const resumenData: any[] = [];
        let totalGeneral = 0;
        const emisoresAgrupados = new Map<string, { facturas: Factura[] }>();

        facturasParaReporte.forEach(factura => {
            if (!emisoresAgrupados.has(factura.emisorNombre)) {
                emisoresAgrupados.set(factura.emisorNombre, { facturas: [] });
            }
            emisoresAgrupados.get(factura.emisorNombre)!.facturas.push(factura);
        });

        emisoresAgrupados.forEach((data, laboratorio) => {
            const importeTotalLaboratorio = data.facturas.reduce((sum, f) => sum + f.total, 0);
            resumenData.push({
                laboratorio: laboratorio,
                periodo: periodoStr,
                importe: importeTotalLaboratorio,
                observaciones: ''
            });
        });

        totalGeneral = facturasParaReporte.reduce((sum, f) => sum + f.total, 0);

        const resumenSheet: SheetsModel = {
            sheetName: 'Resumen',
            header: ['HI-TEC MEDICAL', 'FACTURAS OPERADOR LOGÍSTICO'],
            lineBreak: 1,
            columns: [
                { columnKey: 'laboratorio', columnName: 'LABORATORIO', width: 40 },
                { columnKey: 'periodo', columnName: 'PERIODO', width: 25 },
                { columnKey: 'importe', columnName: 'IMPORTE FACTURA OPERADOR', type: 'currency', width: 30 },
                { columnKey: 'observaciones', columnName: 'OBSERVACIONES', width: 50 },
            ],
            data: resumenData,
            totalRow: { totalLabel: 'Total general', empty1: '', totalValue: totalGeneral, empty2: '' }
        };

        const sheet2Data = facturasParaReporte.map(f => {
            const folioNumerico = String(f.folio).padStart(6, '0');
            const folioFormateado = `${f.serie}/${folioNumerico}`;

            let remisionFolio = '';
            if (f.folioOrden) {
                const parts = f.folioOrden.split('/');
                remisionFolio = parts.length > 1 ? parts[parts.length - 1] : f.folioOrden;
            }

            const ultimoHistorial = f.statusHistory?.slice(-1)[0];

            return {
                cliente: f.receptorNombre,
                factura: folioFormateado,
                cfdiUuid: f.uuid,
                fechaAcuse: f.fechaEmision,
                remision: remisionFolio,
                laboratorio: f.emisorNombre,
                periodo: periodoStr,
                facHitecOperador: '-',
                observaciones: ultimoHistorial?.observacion || '',
                estatus: f.estatus,
                total: f.total,
            };
        });

        const sheet2Struct: ColumnsModel[] = [
            { columnKey: 'cliente', columnName: 'CLIENTE', width: 45 },
            { columnKey: 'factura', columnName: 'FACTURA', width: 20 },
            { columnKey: 'cfdiUuid', columnName: 'CFDI (UUID)', width: 40 },
            { columnKey: 'fechaAcuse', columnName: 'FECHA DE ACUSE', type: 'date', width: 20 },
            { columnKey: 'remision', columnName: 'REMISIÓN', width: 15 },
            { columnKey: 'laboratorio', columnName: 'LABORATORIO', width: 35 },
            { columnKey: 'periodo', columnName: 'PERIODO', width: 25 },
            { columnKey: 'facHitecOperador', columnName: 'FAC HITEC OPERADOR LOGÍSTICO', width: 30 },
            { columnKey: 'observaciones', columnName: 'OBSERVACIONES', width: 50 },
            { columnKey: 'estatus', columnName: 'ESTATUS', width: 25 },
            { columnKey: 'total', columnName: 'TOTAL', type: 'currency', width: 20 },
        ];

        await this.simpleExcel.simpleXLSX({
            fileName: `Cobranza-Operadores-${new Date().toISOString().slice(0, 10)}`,
            sheets: [resumenSheet, { sheetName: 'Detalle', columns: sheet2Struct, data: sheet2Data, header: [], lineBreak: 0 }],
        });
    }

    /**
     * Devuelve una copia de la fecha fin con el día completo (23:59:59.999)
     * para que el filtro sea inclusivo con el día final seleccionado.
     */
    private endOfDay(date: Date | null): Date | null {
        if (!date) return null;
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);
        return end;
    }

    private performSearch() {
        this.isComputingSignal.set(true); // Activar spinner

        // Usamos setTimeout para permitir que la UI se actualice y muestre el spinner antes del cálculo pesado
        setTimeout(() => {
            this.cleanFilter = false;
            this.filterFacturas = structuredClone(this.invoiceDataService.facturasSignal());

            //Filtrar cliente
            let cMatch = this.clients.find((cl) => cl.viewValue === this.clientFilter);
            if (cMatch) this.filterFacturas = this.filterFacturas.filter((item) => item.receptorRFC === cMatch.value);

            //Filtrar emisor
            let eMatch = this.emisores.find((em) => em.viewValue === this.emisorFilter);
            if (eMatch) this.filterFacturas = this.filterFacturas.filter((item) => item.emisorRFC === eMatch.value);

            //Filtrar por fecha de emisión (fecha fin inclusiva: hasta las 23:59:59 del día seleccionado)
            const emisionEnd = this.endOfDay(this.emisionFilter.end);
            this.filterFacturas = this.filterFacturas.filter((item) => {
                const time = new Date(item.fechaEmision);
                if (this.emisionFilter.begin && emisionEnd) return time >= this.emisionFilter.begin && time <= emisionEnd;
                else if (this.emisionFilter.begin) return time >= this.emisionFilter.begin;
                else if (emisionEnd) return time <= emisionEnd;
                else return true;
            });
            //Filtrar por fecha de vencimiento (fecha fin inclusiva)
            const dueEnd = this.endOfDay(this.dueFilter.end);
            this.filterFacturas = this.filterFacturas.filter((item) => {
                const time = new Date(item.fechaVencimiento);
                if (this.dueFilter.begin && dueEnd) return time >= this.dueFilter.begin && time <= dueEnd;
                else if (this.dueFilter.begin) return time >= this.dueFilter.begin;
                else if (dueEnd) return time <= dueEnd;
                else return true;
            });
            this.generateUiData();

            this.isComputingSignal.set(false); // Desactivar spinner
        }, 800); // Aumentado a 800ms para que el spinner sea perceptible visualmente
    }

    goBack(): void {
        this.router.navigate(['/home']);
    }

    forceRefresh() {
        this.customSnackBar.open('Actualizando datos desde el servidor...', 'info');
        this.invoiceDataService.loadInvoices(true).subscribe({
            next: () => {
                this.reloadData(); // Resetea filtros locales tras la carga
                this.customSnackBar.open('Datos actualizados correctamente.', 'success');
            },
            error: () => this.customSnackBar.open('Error al actualizar datos.', 'error')
        });
    }
}
